/**
 * TemplatizedDraftShotsPanel — Renders the `draft_shots` array per scene
 * from the templatized script JSON, plus the "Promote to Shot Breakdown"
 * + "Undo" buttons (Flow A: overwrite + 7-day backup).
 *
 * Why a separate component
 *   ProjectView.js is already ~10.8k lines. This panel is self-contained
 *   (its own polling + state machine for promote/undo) so it lives here.
 *
 * Wired in: ProjectView.js renders this just under the templatized-tab
 * status banner.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { ListChecks, ArrowRight, Undo2, Loader2, Clock, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TemplatizedDraftShotsPanel = ({ project, onPromoted }) => {
  const projectId = project?.id;
  const tj = project?.templatized_script_json;
  const scenes = (tj?.scenes) || [];
  const totalDraftShots = scenes.reduce((acc, s) => acc + ((s.draft_shots || []).length), 0);

  const [undoStatus, setUndoStatus] = useState({ undo_available: false });
  const [busy, setBusy] = useState(false);
  const [expandedScenes, setExpandedScenes] = useState({});

  const fetchUndoStatus = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await axios.get(`${API}/projects/${projectId}/script/templatized/promote-undo-status`);
      setUndoStatus(res.data || { undo_available: false });
    } catch (_e) { /* silent */ }
  }, [projectId]);

  useEffect(() => { fetchUndoStatus(); }, [fetchUndoStatus]);

  const promote = async () => {
    if (!window.confirm(
      `Replace the current Shot Breakdown with ${totalDraftShots} draft shots from the templatized script?\n\n` +
      `This will WIPE all existing shots in this project's Shot Breakdown.\n\n` +
      `A backup is kept for 7 days — you can undo within that window.\n\nProceed?`
    )) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/script/templatized/promote-drafts`);
      alert(
        `Promoted ${res.data.shot_count_after} draft shots into the Shot Breakdown.\n` +
        `Previous breakdown (${res.data.shot_count_before} shots) backed up for 7 days. ` +
        `Click "Undo last promote" to revert.`
      );
      await fetchUndoStatus();
      onPromoted?.();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Promote failed');
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    if (!window.confirm('Restore the Shot Breakdown to its state before the last promote? The current shots will be lost.')) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/script/templatized/undo-promote`);
      alert(`Restored ${res.data.shots_restored} shots from the backup.`);
      await fetchUndoStatus();
      onPromoted?.();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Undo failed');
    } finally {
      setBusy(false);
    }
  };

  if (!tj || scenes.length === 0) return null;
  // Templatize V3 (Production Bible) has no draft_shots — promotion is now
  // Lock → Stage-2 parse, driven from the Production Bible modal. Hide this
  // legacy JSON draft-shots panel for V3 metas; legacy projects keep it.
  if (tj?.kind === 'production_bible' || tj?.schema_version === '3.0') return null;
  if (totalDraftShots === 0) {
    return (
      <div className="mt-3 p-3 rounded-xl border border-white/10 bg-white/[0.02]" data-testid="templatize-drafts-empty">
        <div className="flex items-center gap-2 text-[11px] text-white/50">
          <ListChecks className="w-3.5 h-3.5" />
          <span>No draft shots in this templatize. Re-templatize the script to generate them — the new prompt drafts one shot per visible-action sentence.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 p-3 rounded-xl border border-white/10 bg-white/[0.02]" data-testid="templatize-drafts-panel">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ListChecks className="w-3.5 h-3.5 text-ai-accent" />
          <span className="text-xs font-semibold text-white/80">
            Draft Shot Breakdown
            <span className="ml-2 text-white/40 font-normal">
              {scenes.length} scene{scenes.length === 1 ? '' : 's'} · {totalDraftShots} shot{totalDraftShots === 1 ? '' : 's'}
            </span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          {undoStatus.undo_available && (
            <button
              onClick={undo}
              disabled={busy}
              className="px-2.5 py-1 rounded-md text-[11px] bg-white/5 text-white/70 border border-white/10 hover:bg-white/10 flex items-center gap-1.5 disabled:opacity-40"
              title={`Undo last promote (backup expires in ≤7 days · was ${undoStatus.shot_count_before} → ${undoStatus.shot_count_after} shots)`}
              data-testid="templatize-undo-promote-btn"
            >
              <Undo2 className="w-3 h-3" /> Undo last promote
            </button>
          )}
          <button
            onClick={promote}
            disabled={busy}
            className="px-3 py-1 rounded-md text-[11px] bg-ai-accent/15 text-ai-accent border border-ai-accent/30 hover:bg-ai-accent/25 flex items-center gap-1.5 disabled:opacity-40"
            data-testid="templatize-promote-drafts-btn"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
            Use as Shot Breakdown
          </button>
        </div>
      </div>

      <div className="px-2.5 py-1.5 rounded-md bg-amber-500/5 border border-amber-500/20 text-[10px] text-amber-300/80 flex items-start gap-1.5 mb-2">
        <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
        <span>
          Drafts here are <strong>suggestions</strong>. Promoting overwrites your current Shot Breakdown (a 7-day backup is kept so you can undo).
        </span>
      </div>

      {undoStatus.undo_available && (
        <div className="px-2.5 py-1.5 rounded-md bg-emerald-500/5 border border-emerald-500/20 text-[10px] text-emerald-300/85 flex items-center gap-1.5 mb-2">
          <Clock className="w-3 h-3" />
          <span>Previous breakdown backed up — Undo available for ≤7 days from the last promote.</span>
        </div>
      )}

      <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
        {scenes.map((scene, scIdx) => {
          const ds = scene.draft_shots || [];
          if (ds.length === 0) return null;
          const expanded = expandedScenes[scene.scene_number] !== false; // default open
          return (
            <div key={scene.scene_number || scIdx} className="rounded-lg border border-white/5 overflow-hidden">
              <button
                onClick={() => setExpandedScenes((prev) => ({ ...prev, [scene.scene_number]: !expanded }))}
                className="w-full px-3 py-1.5 flex items-center justify-between bg-white/[0.02] hover:bg-white/[0.04] text-left"
                data-testid={`templatize-scene-toggle-${scene.scene_number}`}
              >
                <span className="text-[11px] font-semibold text-white/80">
                  {scene.scene_number} <span className="text-white/40 font-normal">— {(scene.heading?.location_or_set) || ''}</span>
                </span>
                <span className="text-[10px] text-white/40">{ds.length} shot{ds.length === 1 ? '' : 's'}</span>
              </button>
              {expanded && (
                <div className="divide-y divide-white/5">
                  {ds.map((shot, idx) => (
                    <div key={`${scene.scene_number}-${shot.shot_number}-${idx}`} className="px-3 py-2 text-[11px]">
                      <div className="flex items-baseline gap-2">
                        <span className="font-mono text-[10px] text-ai-accent/80 flex-shrink-0">{shot.shot_number}</span>
                        <span className="text-white/85 leading-snug">{shot.description}</span>
                      </div>
                      {shot.source_sentence && (
                        <div className="mt-0.5 ml-9 text-[10px] text-white/35 italic line-clamp-2">
                          ↳ source: "{shot.source_sentence}"
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TemplatizedDraftShotsPanel;
